/**
 * PaymentsBox — Fee Collection card (سداد الرسوم).
 *
 * iter-264e (Feb 2026) — Full redesign per user request.
 *
 * The card no longer asks "paid to gov" vs "paid to us" at the whole-
 * registration level. Instead each fee row (license + gov entries)
 * carries its own `paid_to` tag which this card reads to compute:
 *
 *   • Amount owed to us = Σ (amount of each fee marked `to_us`)
 *                       + admin_fees.amount (always our income)
 *
 * When the total is zero we show a neutral "لا توجد رسوم للسداد" note.
 * When there is a real amount we require a single receipt whose amount
 * the AI must verify against the computed total before the operator
 * can save.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Landmark, Wallet, Upload, Loader2, CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import { floorAED } from '../../utils/money';
import { SectionCard, SmartSaveButton, ReceiptDownloadLink } from './ui';
import { fileToDataUrl, extractReceipt } from './helpers';

const fmt = (v) => floorAED(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function PaymentsBox({ reg, onSave, t, saving, isAr }) {
  // ── Compute what's owed to us based on per-fee paid_to flags ─────
  const lic = Number(reg?.license_fees?.amount || 0);
  const licPaidTo = reg?.license_fees?.paid_to || null;
  const licToUs = licPaidTo === 'to_us' ? lic : 0;

  const govEntries = reg?.gov_fees?.entries || [];
  const govToUs = govEntries.reduce(
    (s, e) => s + (e.paid_to === 'to_us' ? Number(e.amount || 0) : 0),
    0,
  );
  const govToGov = govEntries.reduce(
    (s, e) => s + (e.paid_to === 'to_gov' ? Number(e.amount || 0) : 0),
    0,
  );

  const afMode = reg?.admin_fees?.mode || null;
  // Only "admin_fees" mode is customer-billed; "commission" mode is what
  // we collect FROM the authority as our channel-partner commission — not
  // from the customer.
  const admin = afMode === 'admin_fees' ? Number(reg?.admin_fees?.amount || 0) : 0;

  const totalDue = floorAED(licToUs + govToUs + admin);
  const totalToGov = floorAED((licPaidTo === 'to_gov' ? lic : 0) + govToGov);

  // ── Persisted receipt (if any) ───────────────────────────────────
  const savedReceipt = reg?.payments_receipt || null;

  // ── Local receipt state ─────────────────────────────────────────
  const [receiptUrl, setReceiptUrl] = useState(savedReceipt?.url || '');
  const [receiptMime, setReceiptMime] = useState(savedReceipt?.mime || '');
  const [receiptAmount, setReceiptAmount] = useState(savedReceipt?.amount || 0);
  const [receiptRef, setReceiptRef] = useState(savedReceipt?.reference || '');
  const [aiVerified, setAiVerified] = useState(!!savedReceipt?.ai_verified);
  const [scanning, setScanning] = useState(false);
  const [aiInfo, setAiInfo] = useState('');

  // Keep local state in sync when the reg reloads (e.g. after a fee edit).
  useEffect(() => {
    setReceiptUrl(savedReceipt?.url || '');
    setReceiptMime(savedReceipt?.mime || '');
    setReceiptAmount(savedReceipt?.amount || 0);
    setReceiptRef(savedReceipt?.reference || '');
    setAiVerified(!!savedReceipt?.ai_verified);
    setAiInfo('');
  }, [savedReceipt?.url, savedReceipt?.mime, savedReceipt?.amount, savedReceipt?.reference, savedReceipt?.ai_verified]);

  const dirty = useMemo(() => (
    (receiptUrl || '') !== (savedReceipt?.url || '') ||
    (receiptMime || '') !== (savedReceipt?.mime || '') ||
    Number(receiptAmount || 0) !== Number(savedReceipt?.amount || 0) ||
    (receiptRef || '') !== (savedReceipt?.reference || '') ||
    !!aiVerified !== !!savedReceipt?.ai_verified
  ), [receiptUrl, receiptMime, receiptAmount, receiptRef, aiVerified, savedReceipt]);

  // ── Upload + AI verification ────────────────────────────────────
  const handleReceipt = async (file) => {
    if (!file) return;
    const url = await fileToDataUrl(file);
    setReceiptUrl(url);
    setReceiptMime(file.type);
    setScanning(true);
    setAiInfo('');
    setAiVerified(false);
    try {
      const data = await extractReceipt(url, file.type, totalDue, 'payments');
      if (!data.is_receipt) {
        setAiInfo(t('❌ الملف ليس إيصالاً: ', '❌ Not a receipt: ') + (data.reject_reason || ''));
        toast.error(t('الملف ليس إيصالاً', 'File is not a receipt'));
        return;
      }
      const readAmt = data.amount ? Number(data.amount) : 0;
      const matches = readAmt > 0 && Math.abs(readAmt - totalDue) < 0.01;
      if (readAmt) setReceiptAmount(readAmt);
      if (data.reference) setReceiptRef(String(data.reference));
      if (matches) {
        setAiVerified(true);
        setAiInfo(t(
          `✓ AI: مبلغ الإيصال ${fmt(readAmt)} = المطلوب ${fmt(totalDue)}`,
          `✓ AI: receipt amount ${fmt(readAmt)} = due ${fmt(totalDue)}`,
        ));
        toast.success(t('✓ تم التحقق من الإيصال', '✓ Receipt verified'));
      } else {
        setAiVerified(false);
        setAiInfo(t(
          `❌ مبلغ الإيصال ${fmt(readAmt)} لا يطابق المطلوب ${fmt(totalDue)}`,
          `❌ Receipt amount ${fmt(readAmt)} does not match due ${fmt(totalDue)}`,
        ));
        toast.error(t('مبلغ الإيصال لا يطابق المطلوب', 'Receipt amount mismatch'));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل قراءة الإيصال', 'OCR failed'));
    } finally {
      setScanning(false);
    }
  };

  const save = () => {
    if (totalDue <= 0) {
      toast.error(t('لا يوجد مبلغ للسداد', 'No amount due'));
      return;
    }
    if (!receiptUrl) {
      toast.error(t('ارفع إيصال السداد', 'Upload the payment receipt'));
      return;
    }
    if (!aiVerified) {
      toast.error(t('يجب أن يتحقق AI من مطابقة الإيصال للمبلغ المطلوب', 'AI must verify receipt matches the due amount'));
      return;
    }
    onSave({
      payments_receipt: {
        url:         receiptUrl,
        mime:        receiptMime,
        amount:      Number(receiptAmount) || totalDue,
        reference:   (receiptRef || '').trim(),
        ai_verified: true,
      },
    });
  };

  // ── Render ──────────────────────────────────────────────────────
  return (
    <SectionCard icon={Wallet} iconColor="#10b981" title={t('سداد الرسوم', 'Fee payment')}>
      {/* Breakdown */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5 text-center">
        <SumTile
          label={t('رسوم الرخصة (لنا)', 'License (to us)')}
          value={licPaidTo === 'to_gov' ? t('مسدد لجهة حكومية', 'To gov') : fmt(licToUs)}
          color="#3b82f6"
          faded={licPaidTo === 'to_gov'}
        />
        <SumTile
          label={t('رسوم حكومية أخرى (لنا)', 'Other gov (to us)')}
          value={govEntries.length === 0
            ? t('لا يوجد', 'None')
            : (govToUs > 0 ? fmt(govToUs) : t('مسدد لجهة حكومية', 'To gov'))}
          color="#06b6d4"
          faded={govEntries.length === 0 || govToUs === 0}
        />
        <SumTile
          label={t('الرسوم الخاصة بنا', 'Our fees')}
          value={fmt(admin)}
          color="#8b5cf6"
        />
        <SumTile
          label={t('المبلغ المطلوب للسداد', 'Total due to us')}
          value={fmt(totalDue)}
          color={totalDue > 0 ? '#10b981' : '#71717A'}
        />
      </div>

      {/* Informative note when everything went straight to government */}
      {totalToGov > 0 && licPaidTo === 'to_gov' && totalDue === 0 && (
        <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/30 text-blue-200 text-sm flex items-center gap-2 mb-4"
             data-testid="pay-note-all-to-gov">
          <Landmark className="h-4 w-4" />
          {t(
            'جميع الرسوم مسددة مباشرة للجهات الحكومية — لا يوجد مبلغ نستلمه من العميل.',
            'All fees paid directly to the government — nothing to collect from the customer.',
          )}
        </div>
      )}

      {/* No amount to collect */}
      {totalDue <= 0 && !(totalToGov > 0 && licPaidTo === 'to_gov') && (
        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm flex items-center gap-2"
             data-testid="pay-no-due">
          <Info className="h-4 w-4 flex-shrink-0" />
          {t(
            'لا توجد رسوم للسداد. اضبط "مسدد لنا" على الرسوم أعلاه أو أدخل الرسوم الخاصة بنا أولاً.',
            'No amount due. Set at least one fee to "Paid to us" or enter our own fees first.',
          )}
        </div>
      )}

      {/* Receipt upload + AI verification */}
      {totalDue > 0 && (
        <div className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-3" data-testid="pay-receipt-block">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-300" />
            <p className="text-sm text-white/80">
              {t(
                'ارفع إيصال السداد. يقوم AI بالتحقق من مطابقة المبلغ للمبلغ المطلوب.',
                'Upload the payment receipt. AI verifies the amount matches the due total.',
              )}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#0a0a0a] border border-[#2a2a4e] text-white cursor-pointer text-sm">
              {scanning ? <Loader2 className="h-4 w-4 animate-spin text-emerald-400" /> : <Upload className="h-4 w-4 text-emerald-400" />}
              {scanning ? t('يقرأ AI...', 'AI reading...') : receiptUrl ? t('تم الرفع ✓', 'Uploaded ✓') : t('رفع الإيصال', 'Upload receipt')}
              <input
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => handleReceipt(e.target.files?.[0])}
                data-testid="pay-receipt-upload"
              />
            </label>
            <div>
              <label className="block text-[10px] text-[#71717A] mb-1">{t('مبلغ الإيصال (AED)', 'Receipt amount (AED)')}</label>
              <input
                type="number"
                value={receiptAmount || ''}
                readOnly
                dir="ltr"
                className="w-full bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-xl px-3 py-2 text-sm cursor-not-allowed"
                data-testid="pay-receipt-amount"
              />
            </div>
            <div>
              <label className="block text-[10px] text-[#71717A] mb-1">{t('رقم المرجع', 'Reference')}</label>
              <input
                value={receiptRef}
                onChange={(e) => setReceiptRef(e.target.value)}
                dir="ltr"
                className="w-full bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-xl px-3 py-2 text-sm"
                data-testid="pay-receipt-ref"
              />
            </div>
          </div>
          {aiInfo && (
            <p className="text-xs" style={{ color: aiVerified ? '#10b981' : '#ef4444' }} data-testid="pay-ai-info">
              {aiInfo}
            </p>
          )}
          {aiVerified && !dirty && savedReceipt && (
            <div className="flex items-center gap-2 text-emerald-300 text-sm" data-testid="pay-verified-badge">
              <CheckCircle2 className="h-4 w-4" />
              {t('تم التحقق من السداد وحفظه.', 'Payment verified and saved.')}
            </div>
          )}
          {savedReceipt?.url ? (
            <ReceiptDownloadLink
              url={savedReceipt.url}
              filename={`payment-receipt-${reg.request_code || reg.id}${(savedReceipt.mime || '').includes('pdf') ? '.pdf' : '.jpg'}`}
              label={t('إيصال سداد الرسوم المحفوظ', 'Saved payment receipt')}
              color="#10b981"
              testId="payments-receipt"
            />
          ) : null}
          <div className="text-end pt-1">
            <SmartSaveButton
              dirty={dirty}
              saving={saving}
              onClick={save}
              t={t}
              color="#10b981"
              hoverColor="#059669"
              data-testid="pay-save"
            />
          </div>
        </div>
      )}
    </SectionCard>
  );
}

// Small pill used above; local so we don't collide with ./ui's SumTile
function SumTile({ label, value, color, faded }) {
  return (
    <div
      className="rounded-xl border p-3"
      style={{
        borderColor: `${color}40`,
        background:  `${color}${faded ? '05' : '10'}`,
        opacity: faded ? 0.65 : 1,
      }}
    >
      <div className="text-[10px] uppercase font-bold tracking-wider mb-1" style={{ color }}>{label}</div>
      <div className="text-white font-mono font-bold text-sm">{value}</div>
    </div>
  );
}
